/*
 * Reveals the email-signup confirmation after a successful native submit and
 * provides one-click loading feedback for native newsletter/contact forms.
 *
 * Shopify's {% form "customer" %} (newsletter) submits to /contact and then
 * REDIRECTS back with `?customer_posted=true`. Unlike the in-place `contact`
 * form, `form.posted_successfully?` does not survive that redirect, and Liquid
 * cannot read the query string — so the server-rendered success branch never
 * fires. This script reads the reliable `customer_posted=true` flag client-side
 * and reveals the (otherwise hidden) confirmation message. A recent signup returns
 * to its saved position; without one, the inline receipt is scrolled into view.
 * The loading-feedback path also covers the native contact form.
 *
 * Shopify's spam-protection challenge page (/challenge) is served with
 * `X-Frame-Options: DENY` + `frame-ancestors 'none'`, so it cannot render inside
 * an iframe. The captcha must run in a top-level navigation, so native submission
 * and its reload are required.
 */
(function nativeFormFeedback() {
  // Shared with newsletter-popup.js; no email addresses are stored in the browser.
  const newsletterStateKey = "wistearia:newsletter";
  const newsletterReturn = Reflect.get(globalThis, "__themeNewsletterReturn");
  let signedUp = false;

  const updateNewsletterVisibility = () => {
    for (const panel of document.querySelectorAll(".newsletter-panel")) {
      if (!(panel instanceof HTMLElement)) {
        continue;
      }
      const section = panel.closest(".shopify-section") ?? panel;
      if (section instanceof HTMLElement) {
        // Keep the receipt readable on this return, including after a CAPTCHA detour.
        // Browser storage only suppresses the popup: it cannot track an unsubscribe
        // or deletion in Shopify. Returning guests must still be able to sign up.
        // A saved-position return keeps the inline panel in layout behind the popup.
        const showingConfirmation = Boolean(panel.querySelector(".email-signup-block--confirmed"));
        section.hidden =
          !globalThis.Shopify?.designMode &&
          ((signedUp && !newsletterReturn) || panel.dataset.subscribed === "true") &&
          !showingConfirmation;
      }
    }
  };

  const rememberNewsletterSignup = () => {
    signedUp = true;
    try {
      localStorage.setItem(newsletterStateKey, "subscribed");
    } catch {
      // The current page still hides further invitations when storage is restricted.
    }
    updateNewsletterVisibility();
  };

  /**
   * Un-hide the confirmation inside a block.
   * @param {Element} block
   * @returns {Element | null} the revealed confirmation, if any
   */
  const revealConfirmation = block => {
    const confirmation = /** @type {HTMLElement | null} */ (block.querySelector("[data-email-signup-confirmation]"));
    if (!confirmation) {
      return null;
    }
    confirmation.hidden = false;
    block.classList.add("email-signup-block--confirmed");

    // Announce to assistive tech. Populating an empty live region is a reliable
    // announce trigger; un-hiding a pre-filled role="status" is not.
    const announce = block.querySelector("[data-email-signup-announce]");
    const text = confirmation.querySelector(".email-signup__message-text");
    if (announce && text) {
      announce.textContent = text.textContent.trim();
    }
    return confirmation;
  };

  const reveal = () => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("customer_posted") !== "true") {
      return;
    }

    // Scope to the block that was actually submitted: Shopify's redirect appends a
    // #EmailSignup-<id> anchor matching that block's form id, so reveal/announce/scroll
    // only that one. Prefer inline blocks if the anchor is missing. (Read the hash
    // before the replaceState below strips it.)
    const submittedId = newsletterReturn?.formId || globalThis.location.hash.slice(1);
    const submittedForm = submittedId ? document.querySelector(`#${CSS.escape(submittedId)}`) : null;
    const submittedBlock = submittedForm?.closest(".email-signup-block");
    const allBlocks = [...document.querySelectorAll(".email-signup-block")];
    const inlineBlocks = allBlocks.filter(block => !block.closest("newsletter-popup"));
    // A missing anchor must not turn an inline signup into an unsolicited modal.
    const fallbackBlocks = inlineBlocks.length > 0 ? inlineBlocks : allBlocks;
    const blocks = submittedBlock ? [submittedBlock] : fallbackBlocks;

    let firstMessage = null;
    for (const block of blocks) {
      const message = revealConfirmation(block);
      firstMessage = firstMessage ?? message;
    }

    if (firstMessage) {
      rememberNewsletterSignup();
    }
    // Restore after confirmation layout settles, but before the popup captures the
    // background position for its scroll lock and eventual close.
    const restored = firstMessage && newsletterReturn?.restore(firstMessage.closest(".email-signup-block"));
    for (const block of blocks) {
      if (block.classList.contains("email-signup-block--confirmed")) {
        document.dispatchEvent(new CustomEvent("email-signup:confirmed", { detail: { block } }));
      }
    }

    if (firstMessage && !restored && !firstMessage.closest("newsletter-popup")) {
      // The post-submit redirect's #EmailSignup-… anchor usually lands the page on
      // the footer already, so only correct the position if the confirmation isn't
      // visible. Deferred so it runs after the theme's own load-time scroll/restoration.
      globalThis.setTimeout(() => {
        const rect = firstMessage.getBoundingClientRect();
        const viewport = globalThis.innerHeight || document.documentElement.clientHeight;
        if (rect.top < 0 || rect.bottom > viewport) {
          firstMessage.scrollIntoView({ behavior: "instant", block: "center" });
        }
      }, 100);
    }

    // Drop the flag (and Shopify's #EmailSignup-… form anchor) so the post-submit
    // URL is clean and a manual refresh doesn't re-show the confirmation.
    params.delete("customer_posted");
    const query = params.toString();
    const { pathname } = globalThis.location;
    globalThis.history.replaceState({}, "", `${pathname}${query ? `?${query}` : ""}`);
  };

  /** @param {Event} event */
  const blockRepeatActivation = event => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute("aria-busy") !== "true") {
      return;
    }
    event.preventDefault();
  };

  /**
   * Show a busy button while a native submit, captcha, and redirect run.
   * The first submit is never intercepted, leaving Shopify's spam flow untouched.
   * Repeat activation of the same busy button is blocked without disabling the
   * form, so Shopify can still complete a programmatic re-submit after captcha.
   *
   * @param {{ formSelector: string, buttonSelector: string, loadingClass: string }} options
   */
  const showLoadingOnSubmit = ({ formSelector, buttonSelector, loadingClass }) => {
    /** @type {{ form: HTMLFormElement, button: HTMLButtonElement }[]} */
    const controls = [];
    /** @type {WeakMap<HTMLFormElement, { form: HTMLFormElement, button: HTMLButtonElement }>} */
    const controlsByForm = new WeakMap();
    // Single shared stall-timer id: only one submit is ever in flight (a submit
    // navigates the page away), and tracking it lets each new submit cancel the
    // previous one's pending clear so an earlier timer can't strip a later
    // submit's spinner. clearLoading also runs on bfcache restore (pageshow).
    /** @type {number | undefined} */
    let stallTimer;
    const clearLoading = () => {
      globalThis.clearTimeout(stallTimer);
      for (const { form, button } of controls) {
        form.removeAttribute("aria-busy");
        button.classList.remove(loadingClass);
        button.removeAttribute("aria-busy");
        button.removeAttribute("aria-disabled");
      }
    };

    // Hoisted out of the loop (rather than an inline closure per form) so the shared
    // stallTimer reference doesn't trip no-loop-func; the submitted form is event.currentTarget.
    /** @param {Event} event */
    const handleSubmit = event => {
      const form = event.currentTarget;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      const control = controlsByForm.get(form);
      if (!control) {
        return;
      }

      const signup = form.closest(".email-signup-block");
      if (signup) {
        try {
          // A modal fixes the body at -scrollY, so window.scrollY alone would save 0.
          const fixedTop = Number.parseFloat(document.body.style.top);
          const y =
            document.body.style.position === "fixed" && Number.isFinite(fixedTop) ? -fixedTop : globalThis.scrollY;
          sessionStorage.setItem(
            "wistearia:newsletter:return",
            JSON.stringify({
              path: location.pathname,
              formId: form.id,
              y: Math.max(0, y),
              height: signup.getBoundingClientRect().height,
              createdAt: Date.now(),
            }),
          );
        } catch {
          // Native form anchors remain the fallback when storage is restricted.
        }
      }

      form.setAttribute("aria-busy", "true");
      control.button.classList.add(loadingClass);
      control.button.setAttribute("aria-busy", "true");
      control.button.setAttribute("aria-disabled", "true");
      // Safety net: a successful submit navigates away (and the spinner dies with the
      // page, or is cleared by the pageshow handler on bfcache restore). But the
      // spam-protection challenge can silently dead-end without navigating — see the
      // header note — which would otherwise leave the button spinning AND unclickable
      // (pointer-events:none) until a manual reload. Real submissions finish in a few
      // seconds; clear well past that so this only ever fires on a genuine stall.
      // Cancel any prior pending timer first so a re-submit (e.g. Enter in the input,
      // which pointer-events:none can't block) doesn't inherit an older 30s deadline.
      globalThis.clearTimeout(stallTimer);
      stallTimer = globalThis.setTimeout(clearLoading, 30_000);
    };

    for (const form of document.querySelectorAll(formSelector)) {
      if (!(form instanceof HTMLFormElement)) {
        continue;
      }
      const button = form.querySelector(buttonSelector);
      if (!(button instanceof HTMLButtonElement)) {
        continue;
      }
      const control = { form, button };
      controls.push(control);
      controlsByForm.set(form, control);
      form.addEventListener("submit", handleSubmit);
      button.addEventListener("click", blockRepeatActivation);
    }

    if (controls.length === 0) {
      return;
    }

    // One listener clears the spinner if the shopper returns via the back button
    // (bfcache restore), rather than one per form.
    globalThis.addEventListener("pageshow", clearLoading);
  };

  const init = () => {
    document.addEventListener("email-signup:confirmed", rememberNewsletterSignup);
    reveal();
    updateNewsletterVisibility();
    globalThis.addEventListener("pageshow", updateNewsletterVisibility);
    globalThis.addEventListener("storage", event => {
      if (event.key === newsletterStateKey || event.key === null) {
        updateNewsletterVisibility();
      }
    });
    document.addEventListener("shopify:section:load", updateNewsletterVisibility);
    showLoadingOnSubmit({
      formSelector: '.email-signup-block form[action*="/contact"]',
      buttonSelector: ".email-signup__button",
      loadingClass: "email-signup__button--loading",
    });
    showLoadingOnSubmit({
      formSelector: '.contact-form__form[action*="/contact"]',
      buttonSelector: ".submit-button",
      loadingClass: "submit-button--loading",
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
